// Access-token refresh for the SPA.
//
// UM issues a short-lived access JWT (Jwt:AccessTokenMinutes, 15 by default) plus a rotating
// refresh token (POST Login/refresh). This module owns the refresh token on the client and the
// one place a refresh happens, so the 401 interceptors on every axios instance, the proactive
// timer in App.tsx and the permission-drift check all share a single in-flight call.
//
// Storage: the refresh token lives in localStorage next to the access token, because the existing
// design is a bearer token in the browser and UM exposes the refresh token only in the JSON body
// (no cookie flow). It is rotated on every use, bound to this browser's deviceId, hashed at rest
// on the server, and a replay revokes the whole session, which bounds what a copied token is worth.
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const REFRESH_KEY = 'refreshToken';
const DEVICE_KEY = 'deviceId';
const LOCK_NAME = 'hrms-token-refresh';

/** Event fired on window after a successful refresh. detail: { token, changed }. */
export const TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';

/** Stable per-browser install id. The server binds a refresh token to it. */
export const getDeviceId = (): string => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'web-unknown';
  }
};

export const storeRefreshToken = (token?: string | null): void => {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
  } catch { /* storage unavailable: the session just cannot be renewed */ }
};

export const hasRefreshToken = (): boolean => {
  try { return !!localStorage.getItem(REFRESH_KEY); } catch { return false; }
};

/** Seconds until the stored access token expires (negative when already expired); null if unreadable. */
export const secondsUntilExpiry = (token: string | null = localStorage.getItem('token')): number | null => {
  if (!token) return null;
  try {
    const exp = (jwtDecode(token) as any)?.exp;
    return typeof exp === 'number' ? exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
};

const claimSet = (token: string | null | undefined): string => {
  if (!token) return '';
  try {
    const d: any = jwtDecode(token);
    const list = (v: any): string[] => (Array.isArray(v) ? v : v ? [v] : []).map(String);
    return JSON.stringify([
      [...new Set(list(d.permission))].sort(),
      [...new Set(list(d.role))].sort(),
    ]);
  } catch {
    return '';
  }
};

/** True when the permission or role claims differ between two access tokens. */
export const claimsDiffer = (a: string | null | undefined, b: string | null | undefined): boolean =>
  claimSet(a) !== claimSet(b);

/** Body of POST Login/refresh (and the token fields of Login/login). Types only; no UI reads the expiry yet. */
export interface RefreshResponse {
  token: string;
  refreshToken: string;
  /** ISO UTC instant after which this refresh chain can no longer be renewed. */
  refreshAbsoluteExpiresAtUtc?: string | null;
}

/** The refresh was refused (token unknown, expired, revoked): the session is over. */
export class RefreshRejectedError extends Error {}
/** The refresh could not be attempted or answered (offline, 5xx): keep the session, try again later. */
export class RefreshUnavailableError extends Error {}

let inflight: Promise<string> | null = null;

const postRefresh = async (refreshToken: string): Promise<RefreshResponse> => {
  const base = (import.meta.env.VITE_USER_BASE_URL as string | undefined) ?? '';
  try {
    const { data } = await axios.post(
      `${base.replace(/\/$/, '')}/Login/refresh`,
      { refreshToken, deviceId: getDeviceId() },
      { timeout: 20000, headers: { 'Content-Type': 'application/json' } }
    );
    if (!data?.token || !data?.refreshToken) throw new RefreshUnavailableError('Malformed refresh response');
    return { token: data.token, refreshToken: data.refreshToken, refreshAbsoluteExpiresAtUtc: data.refreshAbsoluteExpiresAtUtc };
  } catch (e: any) {
    if (e instanceof RefreshUnavailableError) throw e;
    const status = e?.response?.status;
    if (status === 401 || status === 400) throw new RefreshRejectedError(e?.response?.data?.code ?? 'REFRESH_INVALID');
    throw new RefreshUnavailableError(e?.message ?? 'Refresh failed');
  }
};

const performRefresh = async (tokenAtStart: string | null, force: boolean, retried = false): Promise<string> => {
  // Another tab may have refreshed while this one waited for the lock; use its result.
  const current = localStorage.getItem('token');
  if (!force && current && current !== tokenAtStart && (secondsUntilExpiry(current) ?? 0) > 30) return current;

  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new RefreshRejectedError('NO_REFRESH_TOKEN');

  try {
    const result = await postRefresh(refreshToken);
    const previous = localStorage.getItem('token');
    localStorage.setItem('token', result.token);
    localStorage.setItem(REFRESH_KEY, result.refreshToken);
    window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, {
      detail: { token: result.token, changed: claimsDiffer(previous, result.token) },
    }));
    return result.token;
  } catch (e) {
    // Lost a race with another tab that rotated the token we sent: use what it stored, once.
    const latest = localStorage.getItem(REFRESH_KEY);
    if (e instanceof RefreshRejectedError && !retried && latest && latest !== refreshToken) {
      return performRefresh(tokenAtStart, force, true);
    }
    throw e;
  }
};

/**
 * Exchange the refresh token for a new access token. Single-flight in this tab, serialised
 * across tabs with the Web Locks API (refresh tokens rotate, so two tabs presenting the same
 * one would otherwise look like a replay). Resolves with the new access token. Rejects with
 * RefreshRejectedError when the session is over, RefreshUnavailableError on a transient fault.
 * `force` skips the "another tab already refreshed" shortcut (used when permissions drifted).
 */
export const refreshAccessToken = (force = false): Promise<string> => {
  if (inflight) return inflight;
  const tokenAtStart = localStorage.getItem('token');
  const run = () => performRefresh(tokenAtStart, force);
  const locks = (navigator as any).locks;
  const p = (locks?.request ? locks.request(LOCK_NAME, run) : run()) as Promise<string>;
  inflight = p.finally(() => { inflight = null; });
  return inflight;
};

/** Best-effort server-side end of the session (revokes the refresh family). Never throws. */
export const endServerSession = (): void => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const sessionToken = localStorage.getItem('sessionToken');
    if (!refreshToken && !sessionToken) return;
    const base = (import.meta.env.VITE_USER_BASE_URL as string | undefined) ?? '';
    const token = localStorage.getItem('token');
    void axios.post(
      `${base.replace(/\/$/, '')}/Login/logout`,
      { refreshToken, sessionToken },
      { timeout: 8000, headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    ).catch(() => { /* the session is cleared locally regardless */ });
  } catch { /* ignore */ }
};
